import { useState, useEffect, useRef } from "react";
import { api, CONTACT_INFO } from "../lib/api";
import { MessageCircle, X, Send, Sparkles, Phone } from "lucide-react";

const WHATSAPP_DEFAULT = "917002263481";

const GREETING = {
  role: "assistant",
  content:
    "Namaste! I'm EDSW Assistant. May I help you? Ask about our courses, certificate verification, admission, or anything you'd like to learn. 🌿",
};

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [whatsapp, setWhatsapp] = useState(WHATSAPP_DEFAULT);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/site-config").then((r) => {
      if (r.data?.whatsapp_number) setWhatsapp(r.data.whatsapp_number);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const waLink = `https://wa.me/${whatsapp}?text=${encodeURIComponent("Hello EDSW Foundation, I'd like to know more about your courses.")}`;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sessionId,
        messages: next,
      });
      if (data.session_id) setSessionId(data.session_id);
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages([
        ...next,
        { role: "assistant", content: "Sorry, I'm having trouble right now. Please WhatsApp or call +91 7002263481 for quick help." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating action buttons */}
      <div className="fixed bottom-20 right-5 z-[90] flex flex-col items-end gap-3" data-testid="floating-chat">
        {/* WhatsApp button */}
        {showMenu && (
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white pl-4 pr-5 py-3 rounded-full shadow-lg transition-all fade-in"
            data-testid="whatsapp-btn"
          >
            <WhatsappIcon className="w-5 h-5" />
            <span className="text-sm font-semibold whitespace-nowrap">Chat on WhatsApp</span>
          </a>
        )}

        {showMenu && (
          <button
            onClick={() => { setOpen(true); setShowMenu(false); }}
            className="group flex items-center gap-3 bg-[#1B4332] hover:bg-[#2D6A4F] text-white pl-4 pr-5 py-3 rounded-full shadow-lg transition-all fade-in"
            data-testid="open-ai-chat-btn"
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-semibold whitespace-nowrap">Ask EDSW Assistant</span>
          </button>
        )}

        <button
          onClick={() => setShowMenu(!showMenu)}
          className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${
            showMenu ? "bg-[#E07A5F]" : "bg-[#1B4332] hover:bg-[#2D6A4F]"
          } text-white`}
          aria-label="Help menu"
          data-testid="chat-toggle-btn"
        >
          {showMenu ? (
            <X className="w-6 h-6" />
          ) : (
            <>
              <MessageCircle className="w-6 h-6" />
              <span className="absolute top-0 right-0 w-3 h-3 bg-[#E07A5F] rounded-full border-2 border-white animate-pulse" />
            </>
          )}
        </button>

        {!showMenu && !open && (
          <div className="hidden md:block absolute right-16 bottom-1.5 bg-white text-[#1B4332] text-xs font-semibold px-3 py-2 rounded-lg shadow border border-border whitespace-nowrap pointer-events-none">
            May I help you?
            <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-r border-t border-border rotate-45" />
          </div>
        )}
      </div>

      {/* AI Chat Drawer */}
      {open && (
        <div className="fixed inset-0 z-[100] sm:inset-auto sm:bottom-20 sm:right-5 sm:w-[400px] sm:h-[600px] sm:max-h-[80vh] flex flex-col bg-white sm:rounded-2xl shadow-2xl border border-border overflow-hidden fade-in" data-testid="ai-chat-drawer">
          {/* Header */}
          <div className="bg-[#1B4332] text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E07A5F] flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">EDSW Assistant</div>
              <div className="text-[11px] text-stone-300 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Online • Replies instantly
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/10" aria-label="Close" data-testid="ai-chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#F9F8F6] p-4 space-y-3" data-testid="ai-chat-messages">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#1B4332] text-white rounded-br-md"
                      : "bg-white border border-border text-[#222B26] rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-border rounded-2xl rounded-bl-md px-3.5 py-3 flex gap-1.5">
                  <Dot delay="0s" />
                  <Dot delay="0.15s" />
                  <Dot delay="0.3s" />
                </div>
              </div>
            )}
          </div>

          {/* Quick suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {["What courses do you offer?", "How do I verify a certificate?", "Tell me about DCA"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setInput(s); setTimeout(send, 50); }}
                  className="text-xs bg-[#F2EFEB] hover:bg-[#E07A5F] hover:text-white text-[#1B4332] px-3 py-1.5 rounded-full transition-colors"
                  data-testid="ai-chat-suggestion"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-border bg-white flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Type your question..."
              className="flex-1 resize-none border border-border rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#1B4332] max-h-24"
              data-testid="ai-chat-input"
            />
            <button
              onClick={send}
              disabled={!input.trim() || sending}
              className="w-10 h-10 rounded-full bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-40 text-white flex items-center justify-center shrink-0"
              aria-label="Send"
              data-testid="ai-chat-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Footer quick actions */}
          <div className="px-3 pb-3 flex gap-2 bg-white border-t border-border">
            <a href={waLink} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#25D366] hover:bg-[#25D366]/10 py-2 rounded-lg">
              <WhatsappIcon className="w-4 h-4" /> WhatsApp
            </a>
            <a href={`tel:${CONTACT_INFO.phone}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#1B4332] hover:bg-[#1B4332]/10 py-2 rounded-lg">
              <Phone className="w-4 h-4" /> Call Us
            </a>
          </div>
        </div>
      )}
    </>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-2 h-2 rounded-full bg-[#1B4332]/40 animate-bounce"
      style={{ animationDelay: delay }}
    />
  );
}

function WhatsappIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.5 5.282l-.999 3.648 3.988-.629zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );
}
